import {
  dropPicksUnderUnpickedOptions,
  getParentOptionUidByUid,
  isDivider,
} from '~/cynqueModules/onePerSectionSelect'
import { isKitProduct, isSetKitProduct } from '~/types/product/models'
import type { ISupplierProduct } from '~/types/supplierProduct/models'
import type { ISupplierProductItem } from '~/types/supplierProductItem/models'

/**
 * WOULD THESE PICKS BUILD ANYTHING? The rule behind the options choosers'
 * "Select at least one option" checks.
 *
 * A kit builds a row for every mandatory item, and for each option taken. So
 * a kit whose contents are ALL optional builds nothing but its own header
 * unless something in it is picked -- and an empty header is not something the
 * user can have meant. Two cases are refused:
 *
 *   the kit being added   (create time) none of the picks brings a row, and
 *                         neither does anything mandatory in the kit
 *   an optional Kit taken  nothing inside it brings a row: its header alone
 *                         would be built (create time) or kept (after)
 *
 * ⚠ A MANDATORY nested plain Kit COUNTS AS CONTENT, even an empty one: its
 * header is always built (KitService's buildServicesForKitService), so that its
 * own row -- and the options dialog on it, the only place its contents can be
 * chosen later -- exists. Jerry's call, 2026-10-17.
 *
 * Read from the SUPPLIER-PRODUCT TREE, as the options lists are. A
 * catalogue-only option (Add Kit) has no supplier-product, so its loader
 * (services/products.ts) stamps the answers on it as `_hasContent`,
 * `_isPlainKitOption` and `_kitHasUnconditionalContent`; an option without them
 * is read from its own supplier-product item.
 */

/** Does taking this supplier-product item (on its own) bring a row? */
function _doesMandatoryEntryBringARow(item: ISupplierProductItem): boolean {
  if (!isKitProduct(item.product)) {
    return true
  }
  // A slot with no child supplier-product is NOT_AVAILABLE: never built.
  if (!item.childSupplierProduct) {
    return false
  }
  // A Set has no row: only what it holds can bring one.
  if (isSetKitProduct(item.product)) {
    return supplierProductHasUnconditionalContent(item.childSupplierProduct)
  }
  return true
}

/**
 * Does this supplier-product build a row whatever is picked -- a mandatory
 * item, a mandatory plain Kit, or a mandatory Set's mandatory member?
 */
export function supplierProductHasUnconditionalContent(
  supplierProduct: ISupplierProduct | null | undefined
): boolean {
  const items: ISupplierProductItem[] = (supplierProduct as any)?.items || []
  return items.some(
    (item) => !item.isOptional && _doesMandatoryEntryBringARow(item)
  )
}

/**
 * Does taking this option bring a row other than its own header? An item
 * does. An optional Kit or Set does only through what it holds unconditionally;
 * its picks are counted separately.
 */
export function optionHasContent(option: any): boolean | undefined {
  if ('_hasContent' in (option || {})) {
    return !!option._hasContent
  }
  if (!option?.product) {
    return undefined
  }
  if (!isKitProduct(option.product)) {
    return true
  }
  // No child supplier-product (NOT_AVAILABLE), or one not loaded with its
  // items: nothing inside it can be offered either, so say nothing.
  if (!Array.isArray((option.childSupplierProduct as any)?.items)) {
    return undefined
  }
  return supplierProductHasUnconditionalContent(option.childSupplierProduct)
}

/** Is this option a nested plain Kit -- one that would build a header row? */
export function isPlainKitContentOption(option: any): boolean {
  if ('_isPlainKitOption' in (option || {})) {
    return !!option._isPlainKitOption
  }
  return (
    !!option?.product &&
    isKitProduct(option.product) &&
    !isSetKitProduct(option.product)
  )
}

function _getUid(item: any): string {
  return item?._selectUid ?? item?.id
}

export interface IMissingContent {
  /** The kit itself would build nothing but its header (or nothing at all). */
  isKitEmpty: boolean
  /** Optional plain Kits taken with nothing in them. */
  emptyKitOptions: any[]
}

/**
 * Which taken Kits -- and whether the kit itself -- would come out empty.
 *
 * `kitHasUnconditionalContent` is the kit's own answer; `undefined` skips the
 * kit-level check (an options dialog on an existing kit, whose rows are not
 * all in view). Picks under an option not taken are ignored, as the builders
 * ignore them.
 */
export function findMissingContent({
  items,
  picks,
  kitHasUnconditionalContent,
}: {
  items: any[] | null | undefined
  picks: any[] | null | undefined
  kitHasUnconditionalContent?: boolean
}): IMissingContent {
  const options = (items || []).filter((item) => item && !isDivider(item))
  const effectivePicks = dropPicksUnderUnpickedOptions(options, picks || [])
  const parentByUid = getParentOptionUidByUid(options)

  function _isWithin(option: any, ancestorUid: string): boolean {
    let uid: string | undefined = parentByUid.get(_getUid(option))
    while (uid) {
      if (uid === ancestorUid) {
        return true
      }
      uid = parentByUid.get(uid)
    }
    return false
  }

  const contentPicks = effectivePicks.filter(
    (pick) => optionHasContent(pick) === true
  )
  const emptyKitOptions = effectivePicks.filter(
    (pick) =>
      isPlainKitContentOption(pick) &&
      // Unknown content: say nothing rather than refuse.
      optionHasContent(pick) === false &&
      !contentPicks.some((inner) => _isWithin(inner, _getUid(pick)))
  )
  return {
    isKitEmpty:
      kitHasUnconditionalContent === false && contentPicks.length === 0,
    emptyKitOptions,
  }
}

/** A Kit option's name as the user sees it. */
function _getKitOptionName(option: any): string {
  return (
    option?._optionDescr ||
    option?.descr ||
    option?.label ||
    option?.product?.descr ||
    ''
  ).trim()
}

export const NOTHING_TO_ADD_MESSAGE =
  'Nothing would be added: select at least one option'

export function describeMissingContent(
  missing: IMissingContent,
  emptyKitMessage: string = NOTHING_TO_ADD_MESSAGE
): true | string {
  const messages: string[] = []
  if (missing.isKitEmpty) {
    messages.push(emptyKitMessage)
  }
  if (missing.emptyKitOptions.length > 0) {
    messages.push(
      `Select at least one option from: ${missing.emptyKitOptions
        .map(_getKitOptionName)
        .join(', ')}`
    )
  }
  return messages.length > 0 ? messages.join('. ') : true
}

/**
 * The create-time rule, for the Add Services dialogs' options field. The kit's
 * own answer rides on its options as `_kitHasUnconditionalContent` (stamped by
 * both loaders); a list without it gets only the Kit check.
 */
export function validateOptionsBuildContent(
  value: any[] | null,
  items: any[] | null | undefined
): true | string {
  const options = (items || []).filter((item) => item && !isDivider(item))
  if (options.length === 0) {
    return true
  }
  const tagged = options.find(
    (option) => '_kitHasUnconditionalContent' in option
  )
  return describeMissingContent(
    findMissingContent({
      items: options,
      picks: value,
      kitHasUnconditionalContent: tagged
        ? !!tagged._kitHasUnconditionalContent
        : undefined,
    })
  )
}
